import { useState } from 'react';
import { useUserState } from '../../hooks/context/useUserState';
import { useResponseActionsContext } from '../../hooks/context/useResponseActionsContext';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import { fetcher } from '../utils/fetchers/fetcher';
import { resolveApiPath } from '../utils/resolveApiPath';
import { apiRequest } from '../utils/apiRequest';
import { detailedApiMethodFetcher } from '../utils/fetchers/detailedApiMethodFetcher';
import { HandlerRequestContext } from '../types/types';
import { DetailedApiMethod, JSONObject } from '../../types/types';

export const useMatrixDefault = () => {
  const {
    state: { token },
  } = useUserState();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const handleApiError = useApiErrorHandling();
  const { getResponseActionHandler } = useResponseActionsContext();

  const fetch = async (
    api: string,
    forceHeader?: string
  ): Promise<JSONObject | undefined> => {
    try {
      setIsLoading(true);
      setError(undefined);
      return await fetcher<JSONObject>(
        resolveApiPath(api),
        'GET',
        token,
        forceHeader
      );
    } catch (e) {
      if (e instanceof Error) setError(e);
      handleApiError(e, true, (retryForceHeader) =>
        fetch(api, retryForceHeader)
      );
    } finally {
      setIsLoading(false);
    }
  };

  // apiRequest resolves the {{ }} templates against resolveData, fires the
  // request and runs the method's responseActions on the response.
  const detailedFetch = async (
    apiMethod: DetailedApiMethod,
    resolveData: HandlerRequestContext,
    forceHeader?: string
  ): Promise<JSONObject | undefined> => {
    try {
      setIsLoading(true);
      setError(undefined);
      return await apiRequest<JSONObject>(
        apiMethod,
        resolveData,
        (resolvedMethod) =>
          detailedApiMethodFetcher<JSONObject>(resolvedMethod, forceHeader),
        getResponseActionHandler
      );
    } catch (e) {
      if (e instanceof Error) setError(e);
      handleApiError(e, true, (retryForceHeader) =>
        detailedFetch(apiMethod, resolveData, retryForceHeader)
      );
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, fetch, detailedFetch };
};
